import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Driver } from '../../types';
import { useDriverStore } from '../../stores/driverStore';
import theme from '../../theme';

export const DriverDetailsScreen = ({ navigation, route }: any) => {
  const { driverId } = route.params;
  const { drivers, editDriver, loading } = useDriverStore();
  const [isEditing, setIsEditing] = useState(false);
  const [driverData, setDriverData] = useState<Driver | null>(null);

  useEffect(() => {
    const driver = drivers.find(d => d.id === driverId);
    if (driver) {
      setDriverData(driver);
    }
  }, [driverId, drivers]);

  const handleInputChange = (field: keyof Driver, value: string | number) => {
    if (driverData) {
      setDriverData(prev => ({
        ...prev!,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    if (!driverData) return;

    try {
      console.log('💾 Saving driver:', driverData.id);
      await editDriver(driverData.id, driverData);
      setIsEditing(false);
      
      // Navigate back first
      navigation.goBack();
      
      // Show success message after navigation
      setTimeout(() => {
        Alert.alert('نجح / Success', 'تم تحديث السائق بنجاح\nDriver updated successfully');
      }, 300);
    } catch (error) {
      console.error('❌ Error updating driver:', error);
      Alert.alert('خطأ / Error', 'فشل تحديث السائق\nFailed to update driver. Please try again.');
    }
  };

  const handleStatusChange = (newStatus: string) => {
    if (driverData) {
      setDriverData(prev => ({
        ...prev!,
        status: newStatus as Driver['status']
      }));
    }
  };

  if (!driverData) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.title}>Driver Not Found</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>{driverData.name}</Text>
        <TouchableOpacity
          onPress={() => setIsEditing(!isEditing)}
          style={styles.editButton}
        >
          <Ionicons 
            name={isEditing ? "close" : "pencil"} 
            size={20} 
            color={theme.colors.primary} 
          />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content}>
        {/* Driver Image */}
        {driverData.image_url && (
          <Card style={styles.imageCard}>
            <Image source={{ uri: driverData.image_url }} style={styles.driverImage} />
          </Card>
        )}

        {/* Personal Information */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Personal Information</Text>
          
          <View style={styles.field}>
            <Text style={styles.label}>Full Name</Text>
            <TextInput
              style={[styles.input, !isEditing && styles.inputDisabled]}
              value={driverData.name}
              onChangeText={(value) => handleInputChange('name', value)}
              editable={isEditing}
            />
          </View>

          <View style={styles.row}>
            <View style={styles.field}>
              <Text style={styles.label}>Phone Number</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={driverData.phone}
                onChangeText={(value) => handleInputChange('phone', value)}
                editable={isEditing}
                keyboardType="phone-pad"
              />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Nationality</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={driverData.nationality || ''}
                onChangeText={(value) => handleInputChange('nationality', value)}
                editable={isEditing}
              />
            </View>
          </View>
        </Card>

        {/* License Information */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>License Information</Text>
          
          <View style={styles.row}>
            <View style={styles.field}>
              <Text style={styles.label}>License Number</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={driverData.licenseNumber}
                onChangeText={(value) => handleInputChange('licenseNumber', value)}
                editable={isEditing}
              />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>License Expiry</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={driverData.licenseExpiry}
                onChangeText={(value) => handleInputChange('licenseExpiry', value)}
                editable={isEditing}
                placeholder="YYYY-MM-DD"
              />
            </View>
          </View>
        </Card>

        {/* Status & Assignment */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Status & Assignment</Text>
          
          <View style={styles.field}>
            <Text style={styles.label}>Current Status</Text>
            {isEditing ? (
              <View style={styles.statusButtons}>
                {['active', 'inactive', 'suspended'].map((status) => (
                  <TouchableOpacity
                    key={status}
                    style={[
                      styles.statusButton,
                      driverData.status === status && styles.statusButtonActive
                    ]}
                    onPress={() => handleStatusChange(status)}
                  >
                    <Text style={[
                      styles.statusButtonText,
                      driverData.status === status && styles.statusButtonTextActive
                    ]}>
                      {status.toUpperCase()}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              <View style={[styles.statusDisplay, { backgroundColor: getStatusColor(driverData.status) }]}>
                <Text style={styles.statusDisplayText}>
                  {driverData.status.toUpperCase()}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Assigned Vehicle</Text>
            <TextInput
              style={[styles.input, !isEditing && styles.inputDisabled]}
              value={driverData.currentVehicleId || 'Not Assigned'}
              onChangeText={(value) => handleInputChange('currentVehicleId', value)}
              editable={isEditing}
              placeholder="Vehicle ID or License Plate"
            />
          </View>

          <View style={styles.row}>
            <View style={styles.field}>
              <Text style={styles.label}>Daily Target (AED)</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={String(driverData.dailyTarget || '')}
                onChangeText={(value) => handleInputChange('dailyTarget', parseFloat(value) || 0)}
                editable={isEditing}
                keyboardType="numeric"
                placeholder="0"
              />
            </View>
            <View style={styles.field}>
              <Text style={styles.label}>Deposit (AED)</Text>
              <TextInput
                style={[styles.input, !isEditing && styles.inputDisabled]}
                value={String(driverData.deposit || '')}
                onChangeText={(value) => handleInputChange('deposit', parseFloat(value) || 0)}
                editable={isEditing}
                keyboardType="numeric"
                placeholder="0"
              />
            </View>
          </View>
        </Card>

        {/* Additional Information */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Additional Information</Text>
          
          <View style={styles.field}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea, !isEditing && styles.inputDisabled]}
              value={driverData.notes || ''}
              onChangeText={(value) => handleInputChange('notes', value)}
              editable={isEditing}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
              placeholder="Add any additional notes about this driver..."
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Registration Date</Text>
            <Text style={styles.dateText}>
              {new Date(driverData.created_at).toLocaleDateString()}
            </Text>
          </View>
        </Card>

        {/* Quick Actions */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Quick Actions</Text>
          
          <View style={styles.quickActions}>
            <TouchableOpacity style={styles.actionButton}>
              <Ionicons name="call" size={20} color={theme.colors.primary} />
              <Text style={styles.actionButtonText}>Call Driver</Text>
            </TouchableOpacity>
            
            <TouchableOpacity style={styles.actionButton}>
              <Ionicons name="car" size={20} color={theme.colors.primary} />
              <Text style={styles.actionButtonText}>Assign Vehicle</Text>
            </TouchableOpacity>
            
            <TouchableOpacity style={styles.actionButton}>
              <Ionicons name="calculator" size={20} color={theme.colors.primary} />
              <Text style={styles.actionButtonText}>View Financials</Text>
            </TouchableOpacity>
          </View>
        </Card>

        {/* Action Buttons */}
        {isEditing && (
          <View style={styles.actionButtons}>
            <Button
              title={loading ? "Saving..." : "Save Changes"}
              onPress={handleSave}
              disabled={loading}
              variant="primary"
              style={styles.saveButton}
            />
            <Button
              title="Cancel"
              onPress={() => {
                setIsEditing(false);
                // Reset to original data
                const originalDriver = drivers.find(d => d.id === driverId);
                if (originalDriver) {
                  setDriverData(originalDriver);
                }
              }}
              variant="secondary"
              style={styles.cancelButton}
            />
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return theme.colors.success;
    case 'inactive':
      return theme.colors.warning;
    case 'suspended':
      return theme.colors.error;
    default:
      return theme.colors.gray400;
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  title: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    flex: 1,
    marginLeft: theme.spacing.md,
  },
  editButton: {
    padding: theme.spacing.sm,
  },
  content: {
    flex: 1,
    padding: theme.spacing.md,
  },
  imageCard: {
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.md,
    alignItems: 'center',
  },
  driverImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
    resizeMode: 'cover',
  },
  section: {
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  row: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  field: {
    flex: 1,
    marginBottom: theme.spacing.md,
  },
  label: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    borderRadius: theme.borderRadius.md,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textPrimary,
    backgroundColor: theme.colors.white,
  },
  inputDisabled: {
    backgroundColor: theme.colors.gray50,
    color: theme.colors.textSecondary,
  },
  textArea: {
    height: 80,
    paddingTop: theme.spacing.sm,
  },
  dateText: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    paddingVertical: theme.spacing.sm,
  },
  statusButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
  },
  statusButton: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.round,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    backgroundColor: theme.colors.white,
  },
  statusButtonActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  statusButtonText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
  },
  statusButtonTextActive: {
    color: theme.colors.white,
  },
  statusDisplay: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
  },
  statusDisplayText: {
    color: theme.colors.white,
    fontWeight: '500' as any,
    fontSize: theme.typography.sizes.sm,
  },
  quickActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.white,
    gap: theme.spacing.xs,
  },
  actionButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.primary,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginVertical: theme.spacing.lg,
  },
  saveButton: {
    flex: 1,
  },
  cancelButton: {
    flex: 1,
  },
});

export default DriverDetailsScreen;
